import React, { useCallback, useEffect, useState, useRef, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, MapPinIcon, SendIcon, XIcon, DownloadIcon, ShareIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, PackageIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useRestaurant } from '../context/RestaurantContext';
import { CheckoutData } from '../types/types';
import { OrderTicket } from './OrderTicket';
import { LocationPicker } from './LocationPicker';
import html2canvas from 'html2canvas';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { createOrder, createOrderItems, getClientFingerprint } from '../lib/restaurant';
interface CheckoutProps {
  onBack: () => void;
}
export function Checkout({
  onBack
}: CheckoutProps) {
  const {
    items,
    cartTotal,
    clearCart
  } = useCart();
  const {
    t,
    formatPrice
  } = useLanguage();
  const {
    restaurant,
    restaurantId,
    products
  } = useRestaurant();
  const currencySymbol = restaurant?.currency_symbol || '₡';
  const whatsappNumber = restaurant?.whatsapp_number || '';
  const deliveryFee = restaurant?.delivery_fee || 0;
  const appName = restaurant?.name || '';
  const adminPin = restaurant?.admin_pin || '1234';
  const cloudName = restaurant?.cloudinary_cloud_name || '';
  const uploadPreset = restaurant?.cloudinary_upload_preset || '';
  const makeWebhookUrl = restaurant?.make_webhook_url || null;
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);
  const [data, setData] = useState<CheckoutData>(() => {
    try {
      const saved = localStorage.getItem('customer_data');
      if (saved) {
        const customer = JSON.parse(saved);
        return {
          nombre: customer.name || '',
          telefono: customer.phone || '',
          email: customer.email || '',
          comentarios: '',
          alergias: '',
          entrega: 'Retiro en local' as const,
          direccion: customer.address || '',
          ubicacion: customer.location_url || '',
          metodoPago: '' as const,
          montoEfectivo: ''
        };
      }
    } catch (e) {
      console.error('Error reading customer data:', e);
    }
    return {
      nombre: '',
      telefono: '',
      email: '',
      comentarios: '',
      alergias: '',
      entrega: 'Retiro en local',
      direccion: '',
      ubicacion: '',
      metodoPago: '',
      montoEfectivo: ''
    };
  });
  const [showTicket, setShowTicket] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [ticketImage, setTicketImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [savedTrackingToken, setSavedTrackingToken] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [recentOrder, setRecentOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    timestamp: number;
  } | null>(() => {
    try {
      const saved = localStorage.getItem('recent_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        const elapsed = Date.now() - parsed.timestamp;
        const TEN_MINUTES = 10 * 60 * 1000;
        if (elapsed < TEN_MINUTES) return parsed;
        localStorage.removeItem('recent_order');
      }
    } catch (e) {}
    return null;
  });
  const [dismissedRecentOrder, setDismissedRecentOrder] = useState(false);
  const [demandLevel, setDemandLevel] = useState<'normal' | 'high' | 'very-high'>('normal');
  const [showDemandPopup, setShowDemandPopup] = useState(false);
  const [demandAcknowledged, setDemandAcknowledged] = useState(false);
  const ticketRef = useRef<HTMLDivElement>(null);
  const tableNumber = new URLSearchParams(window.location.search).get('table');
  const totalFinal = cartTotal + (!tableNumber && data.entrega === 'Express' ? deliveryFee : 0);
  const totalFormatted = formatPrice(totalFinal);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = data.nombre.trim() !== '' && data.telefono.trim() !== '' && (isReturningCustomer || tableNumber || data.email.trim() !== '' && emailRegex.test(data.email)) && items.length > 0 && (tableNumber || data.metodoPago !== '') && (tableNumber || data.entrega === 'Retiro en local' || data.ubicacion !== '');
  const estimatedMinutes = tableNumber ? 15 : data.entrega === 'Express' ? 35 : 20;
  useEffect(() => {
    try {
      const saved = localStorage.getItem('customer_data');
      if (saved) {
        const customer = JSON.parse(saved);
        if (customer.email) {
          setIsReturningCustomer(true);
        }
      }
    } catch (e) {
      console.error('Error checking customer data:', e);
    }
  }, []);
  // Check demand level — filtered by restaurant_id
  useEffect(() => {
    if (!restaurantId) return;
    const checkDemand = async () => {
      try {
        const {
          count,
          error
        } = await supabase.from('orders').select('id', {
          count: 'exact'
        }).eq('restaurant_id', restaurantId).in('status', ['accepted', 'preparing']);
        if (error) throw error;
        const activeCount = count || 0;
        if (activeCount >= 6) {
          setDemandLevel('very-high');
          setShowDemandPopup(true);
        } else if (activeCount >= 3) {
          setDemandLevel('high');
          setShowDemandPopup(true);
        }
      } catch (err) {
        console.error('Error checking demand level:', err);
      }
    };
    checkDemand();
  }, [restaurantId]);
  useEffect(() => {
    if (!showConfirmation) return;
    const timer = setTimeout(() => {
      clearCart();
      onBack();
    }, 300000);
    return () => clearTimeout(timer);
  }, [showConfirmation, clearCart, onBack]);
  const generateOrderNumber = () => {
    const now = new Date();
    const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const timePart = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `AV-${datePart}-${timePart}${random}`;
  };
  const generateTicketImage = useCallback(async () => {
    if (!ticketRef.current) return null;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#0F0F0F',
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Error generating ticket image:', err);
      return null;
    }
  }, []);
  const dataUrlToBlob = async (dataUrl: string) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };
  const uploadTicketToCloudinary = async (dataUrl: string, ordNum: string) => {
    if (!cloudName || !uploadPreset) {
      console.warn('Cloudinary not configured for this restaurant');
      return '';
    }
    const blob = await dataUrlToBlob(dataUrl);
    const formData = new FormData();
    formData.append('file', blob);
    formData.append('upload_preset', uploadPreset);
    formData.append('folder', 'pedidos');
    formData.append('public_id', ordNum);
    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
      method: 'POST',
      body: formData
    });
    const result = await response.json();
    return result.secure_url as string;
  };
  const sendOrderToMake = async ({
    orderNumber: ordNum,
    imageUrl
  }: {
    orderNumber: string;
    imageUrl: string;
  }) => {
    if (!makeWebhookUrl) return;
    try {
      const payload = {
        restaurant_id: restaurantId,
        orderId: ordNum,
        customerName: data.nombre,
        customerPhone: data.telefono,
        deliveryType: data.entrega,
        address: data.direccion,
        locationUrl: data.ubicacion,
        paymentMethod: data.metodoPago,
        subtotal: cartTotal,
        expressFee: data.entrega === 'Express' ? deliveryFee : 0,
        totalFinal: totalFinal,
        restaurantPhone: whatsappNumber,
        restaurantName: appName,
        orderText: generateFullTextMessage(),
        imageUrl,
        createdAt: new Date().toISOString()
      };
      await fetch(makeWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('Error enviando pedido a Make:', error);
    }
  };
  const buildOrderText = (ordNum: string) => {
    let orderText = `Pedido #${ordNum} — ${data.nombre} — ${data.telefono}\n`;
    orderText += `Entrega: ${data.entrega}\n`;
    items.forEach(item => {
      orderText += `${item.name} x${item.quantity} — ${currencySymbol}${(item.subtotal * item.quantity).toLocaleString()}\n`;
      const s = item.selections;
      if (s && typeof s === 'object') {
        if (s.base && s.base.name) orderText += `  Base: ${s.base.name}\n`;
        if (s.proteina && s.proteina.name) orderText += `  Proteina: ${s.proteina.name}\n`;
        if (Array.isArray(s.toppings) && s.toppings.length > 0) {
          orderText += `  Toppings: ${s.toppings.map((t: any) => t.name || t).join(', ')}\n`;
        }
        if (Array.isArray(s.toppingsPremium) && s.toppingsPremium.length > 0) {
          orderText += `  Premium: ${s.toppingsPremium.map((t: any) => t.name || t).join(', ')}\n`;
        }
        if (Array.isArray(s.aderezos) && s.aderezos.length > 0) {
          orderText += `  Aderezo: ${s.aderezos.map((t: any) => t.name || t).join(', ')}\n`;
        }
        if (Array.isArray(s.semillas) && s.semillas.length > 0) {
          orderText += `  Semillas: ${s.semillas.map((t: any) => t.name || t).join(', ')}\n`;
        }
        if (s.comentarios) orderText += `  Notas: ${s.comentarios}\n`;
      }
    });
    orderText += `Total: ${currencySymbol}${totalFinal.toLocaleString()}`;
    return orderText;
  };
  const handleSendOrder = async () => {
    if (!isValid || !restaurantId) return;
    try {
      setIsGenerating(true);
      const newOrderNumber = generateOrderNumber();
      const trackingToken = crypto.randomUUID();
      const clientFingerprint = getClientFingerprint();
      setOrderNumber(newOrderNumber);
      setSavedTrackingToken(trackingToken);
      setShowConfirmation(true);
      await new Promise(resolve => setTimeout(resolve, 400));
      const imageData = await generateTicketImage();
      setTicketImage(imageData);
      if (!imageData) {
        console.error('Could not generate ticket image');
        return;
      }
      // 1) Upload to Cloudinary
      const imageUrl = await uploadTicketToCloudinary(imageData, newOrderNumber);
      // 2) Build order text
      const orderText = buildOrderText(newOrderNumber);
      const table = new URLSearchParams(window.location.search).get('table');
      const deliveryTypeValue = data.entrega === 'Express' ? 'express' : 'pickup';
      // 3) Create order via typed helper (with restaurant_id)
      let insertedOrder: any = null;
      try {
        insertedOrder = await createOrder({
          restaurant_id: restaurantId,
          order_number: newOrderNumber,
          tracking_token: trackingToken,
          client_fingerprint: clientFingerprint,
          customer_name: data.nombre,
          customer_phone: data.telefono,
          customer_email: data.email || undefined,
          delivery_type: deliveryTypeValue as 'express' | 'pickup',
          address: data.direccion || null,
          location_url: data.ubicacion || null,
          payment_method: data.metodoPago,
          subtotal: cartTotal,
          express_fee: data.entrega === 'Express' ? deliveryFee : 0,
          total_final: totalFinal,
          image_url: imageUrl,
          order_text: orderText,
          table_number: table || null,
          service_mode: table ? 'qr' : null
        });
      } catch (supabaseError: any) {
        console.error('Error saving order to Supabase:', supabaseError);
      }
      // 4) Create order_items (with restaurant_id)
      if (insertedOrder?.id) {
        try {
          const orderItems = items.map(item => {
            // Find the product's station from the Supabase products
            const supaProduct = products.find(p => p.id === item.productId);
            return {
              restaurant_id: restaurantId,
              order_id: insertedOrder.id,
              product_name: item.name,
              quantity: item.quantity,
              price: item.subtotal,
              station: supaProduct?.station || null,
              notes: item.selections?.comentarios || null
            };
          });
          await createOrderItems(orderItems);
        } catch (itemsError) {
          console.error('Error creating order items:', itemsError);
        }
      }
      // 5) Save/update customer
      try {
        const customerData = {
          name: data.nombre,
          phone: data.telefono,
          email: data.email || null,
          address: data.direccion || null,
          location_url: data.ubicacion || null
        };
        localStorage.setItem('customer_data', JSON.stringify(customerData));
        await supabase.from('customers').upsert({
          phone: data.telefono,
          name: data.nombre,
          email: data.email || null,
          address: data.direccion || null,
          location_url: data.ubicacion || null,
          restaurant_id: restaurantId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'phone'
        });
        await supabase.rpc('increment_customer_orders', {
          customer_phone: data.telefono
        }).catch(() => {});
      } catch (custErr) {
        console.error('Error saving customer (non-critical):', custErr);
      }
      // 6) Send to Make webhook
      try {
        await sendOrderToMake({
          orderNumber: newOrderNumber,
          imageUrl
        });
      } catch (makeError) {
        console.error('Error sending to Make (non-critical):', makeError);
      }
      // 7) Save recent order
      try {
        localStorage.setItem('recent_order', JSON.stringify({
          orderNumber: newOrderNumber,
          trackingToken,
          timestamp: Date.now()
        }));
      } catch (e) {}
      // 8) Build WhatsApp message
      let whatsMsg = `🔥 *NUEVO PEDIDO — ${appName}*\n\n`;
      whatsMsg += `*Pedido #${newOrderNumber}*\n\n`;
      whatsMsg += `👤 *Cliente:* ${data.nombre}\n`;
      whatsMsg += `📞 *Tel:* ${data.telefono}\n\n`;
      whatsMsg += `🛒 *PEDIDO:*\n`;
      items.forEach((item, index) => {
        whatsMsg += `${index + 1}. *${item.name}* (x${item.quantity}) — ${currencySymbol}${(item.subtotal * item.quantity).toLocaleString()}\n`;
        const s = item.selections;
        if (s.base) whatsMsg += `   Base: ${s.base.name}\n`;
        if (s.proteina) whatsMsg += `   Proteína: ${s.proteina.name}\n`;
        if (s.toppings?.length) whatsMsg += `   Toppings: ${s.toppings.map((t: any) => t.name).join(', ')}\n`;
        if (s.toppingsPremium?.length) whatsMsg += `   Premium: ${s.toppingsPremium.map((t: any) => t.name).join(', ')}\n`;
        if (s.aderezos?.length) whatsMsg += `   Aderezos: ${s.aderezos.map((t: any) => t.name).join(', ')}\n`;
        if (s.comentarios) whatsMsg += `   Notas: ${s.comentarios}\n`;
      });
      whatsMsg += `\n💰 *Subtotal:* ${currencySymbol}${cartTotal.toLocaleString()}\n`;
      if (data.entrega === 'Express') {
        whatsMsg += `🛵 *Express:* ${currencySymbol}${deliveryFee.toLocaleString()}\n`;
      }
      whatsMsg += `✅ *TOTAL: ${currencySymbol}${totalFinal.toLocaleString()}*\n\n`;
      whatsMsg += `📦 *Entrega:* ${data.entrega === 'Express' ? 'Express 🛵' : 'Retiro en local 🏪'}\n`;
      whatsMsg += `💳 *Pago:* ${data.metodoPago}\n`;
      if (data.metodoPago === 'Efectivo' && data.montoEfectivo) {
        whatsMsg += `💵 *Paga con:* ${currencySymbol}${data.montoEfectivo}\n`;
      }
      if (data.entrega === 'Express') {
        if (data.direccion) whatsMsg += `\n🏠 *Dirección:*\n${data.direccion}\n`;
        if (data.ubicacion) whatsMsg += `\n📍 *Ubicación:*\n${data.ubicacion}\n`;
      }
      if (data.comentarios) whatsMsg += `\n📝 *Notas:* ${data.comentarios}\n`;
      if (data.alergias) whatsMsg += `⚠️ *Alergias:* ${data.alergias}\n`;
      if (imageUrl) whatsMsg += `\n🧾 *Ticket:*\n${imageUrl}\n`;
      const encoded = encodeURIComponent(whatsMsg);
      setWhatsappUrl(`https://wa.me/${whatsappNumber}?text=${encoded}`);
    } catch (error) {
      console.error('Error in handleSendOrder:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  const generateFullTextMessage = () => {
    let msg = `Hola, quiero realizar este pedido en *${appName}* 🔥\n\n`;
    msg += `*PEDIDO #${orderNumber}*\n\n`;
    msg += `*DATOS DEL CLIENTE*\n`;
    msg += `Nombre: ${data.nombre}\n`;
    msg += `Teléfono: ${data.telefono}\n\n`;
    msg += `*TIPO DE ENTREGA*\n`;
    msg += `Entrega: ${data.entrega}\n`;
    if (data.entrega === 'Express') {
      if (data.direccion) msg += `Dirección: ${data.direccion}\n`;
      if (data.ubicacion) {
        const coordMatch = data.ubicacion.match(/q=([-\d.]+),([-\d.]+)/);
        if (coordMatch) {
          const lat = coordMatch[1];
          const lng = coordMatch[2];
          msg += `\n📍 *UBICACIÓN DEL CLIENTE*\n`;
          msg += `Google Maps: https://www.google.com/maps?q=${lat},${lng}\n`;
          msg += `Waze: https://waze.com/ul?ll=${lat},${lng}&navigate=yes\n`;
        } else {
          msg += `Ubicación: ${data.ubicacion}\n`;
        }
      }
      msg += `Cargo express: ${currencySymbol}${deliveryFee}\n`;
    }
    msg += `\n*MÉTODO DE PAGO*\n`;
    msg += `Pago: ${data.metodoPago}\n`;
    if (data.metodoPago === 'Efectivo' && data.montoEfectivo) {
      msg += `Paga con: ${currencySymbol}${data.montoEfectivo}\n`;
      const vuelto = parseInt(data.montoEfectivo) - totalFinal;
      if (vuelto > 0) msg += `Vuelto para: ${currencySymbol}${vuelto}\n`;
    }
    msg += `\n*PEDIDO*\n`;
    items.forEach((item, index) => {
      msg += `${index + 1}. *${item.name}* (x${item.quantity})\n`;
      const s = item.selections;
      if (s.base) msg += `   - Base: ${s.base.name}\n`;
      if (s.proteina) msg += `   - Proteína: ${s.proteina.name}\n`;
      if (s.toppings?.length) msg += `   - Toppings: ${s.toppings.map(t => t.name).join(', ')}\n`;
      if (s.toppingsPremium?.length) msg += `   - Premium: ${s.toppingsPremium.map(t => t.name).join(', ')}\n`;
      if (s.aderezos?.length) msg += `   - Aderezos: ${s.aderezos.map(t => t.name).join(', ')}\n`;
      if (s.semillas?.length) msg += `   - Semillas: ${s.semillas.map(t => t.name).join(', ')}\n`;
      if (s.comentarios) msg += `   - Notas: ${s.comentarios}\n`;
      msg += `   - Subtotal: ${currencySymbol}${(item.subtotal * item.quantity).toLocaleString()}\n\n`;
    });
    if (data.comentarios || data.alergias) {
      msg += `*COMENTARIOS GENERALES*\n`;
      if (data.alergias) msg += `Alergias/Restricciones: ${data.alergias}\n`;
      if (data.comentarios) msg += `Notas: ${data.comentarios}\n`;
      msg += `\n`;
    }
    msg += `*TOTAL A PAGAR*\n`;
    msg += `Subtotal: ${currencySymbol}${cartTotal.toLocaleString()}\n`;
    if (data.entrega === 'Express') {
      msg += `Express: ${currencySymbol}${deliveryFee.toLocaleString()}\n`;
    }
    msg += `*Total final: ${currencySymbol}${totalFinal.toLocaleString()}*\n\n`;
    msg += `Gracias 🙏`;
    return msg;
  };
  const handleDownloadTicket = () => {
    if (!ticketImage) return;
    const link = document.createElement('a');
    link.download = `pedido-${orderNumber}.png`;
    link.href = ticketImage;
    link.click();
  };
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: 50
  }} className="fixed inset-0 z-40 bg-carbon flex flex-col max-w-md mx-auto" data-id="element-767">
      <div className="bg-surface px-4 py-4 flex items-center shadow-sm z-10 border-b border-gray-800" data-id="element-768">
        <button onClick={onBack} className="p-2 -ml-2 text-gray-400 hover:text-white" data-id="element-769">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-770" />
        </button>
        <h2 className="font-heading font-semibold text-lg ml-2 text-white tracking-wide" data-id="element-771">
          {tableNumber ? `🍽️ Pedido Mesa ${tableNumber}` : t('finishOrder')}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-32 space-y-6" data-id="element-772">
        {/* Duplicate Order Warning */}
        {recentOrder && !dismissedRecentOrder && !showConfirmation && <motion.section initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-amber-500/10 border border-amber-500/25 p-5 rounded-2xl" data-id="element-773">
            <div className="flex items-start gap-3 mb-3" data-id="element-774">
              <span className="text-2xl" data-id="element-775">⚠️</span>
              <div data-id="element-776">
                <h3 className="font-heading font-semibold text-amber-400 mb-1" data-id="element-777">
                  Ya realizaste un pedido
                </h3>
                <p className="text-sm text-gray-300" data-id="element-778">
                  Tu pedido{' '}
                  <span className="font-bold text-white" data-id="element-779">
                    #{recentOrder.orderNumber}
                  </span>{' '}
                  está en preparación.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-4" data-id="element-780">
              <a href={`${window.location.origin}/track/${recentOrder.trackingToken}`} className="w-full py-3 bg-dorado text-carbon font-bold rounded-xl text-center text-sm hover:bg-dorado/90 transition-colors flex items-center justify-center gap-2" data-id="element-781">
                📦 Ver estado de mi pedido
              </a>
              <button onClick={() => setDismissedRecentOrder(true)} className="w-full py-3 bg-surface-light text-gray-400 font-medium rounded-xl text-center text-sm border border-gray-700 hover:text-white hover:border-gray-600 transition-colors" data-id="element-782">
                Quiero hacer un nuevo pedido
              </button>
            </div>
          </motion.section>}

        {/* Datos Personales */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-783">
          <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-784">
            {t('yourData')}
          </h3>
          {isReturningCustomer ? <p className="text-xs text-verde bg-verde/10 border border-verde/20 rounded-lg px-3 py-2 mb-4 flex items-center gap-2" data-id="element-785">
              <span data-id="element-786">👋</span> ¡Hola de nuevo, {data.nombre.split(' ')[0]}! Tus
              datos están guardados.
            </p> : <p className="text-xs text-gray-400 mb-4" data-id="element-787">{t('dataNeeded')}</p>}
          <div className="space-y-3" data-id="element-788">
            <div data-id="element-789">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-790">
                {t('fullName')} <span className="text-terracota" data-id="element-791">*</span>
              </label>
              <input type="text" value={data.nombre} onChange={e => setData({
              ...data,
              nombre: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('namePlaceholder')} data-id="element-792" />
            </div>
            <div data-id="element-793">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-794">
                {t('phone')} <span className="text-terracota" data-id="element-795">*</span>
              </label>
              <input type="tel" value={data.telefono} onChange={e => setData({
              ...data,
              telefono: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('phonePlaceholder')} data-id="element-796" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-797">{t('phoneHint')}</p>
            </div>
            {!isReturningCustomer && <div data-id="element-798">
                <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-799">
                  Email <span className="text-terracota" data-id="element-800">*</span>
                </label>
                <input type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" value={data.email} onChange={e => setData({
              ...data,
              email: e.target.value
            })} className={`w-full p-3 bg-surface-light border rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500 ${data.email && !emailRegex.test(data.email) ? 'border-red-500/50' : 'border-gray-700'}`} placeholder="tu@email.com" data-id="element-801" />
                {data.email && !emailRegex.test(data.email) && <p className="text-xs text-red-400 mt-1" data-id="element-802">
                    Ingresá un correo válido (ej: nombre@email.com)
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-803">
                  Para enviarte ofertas y promociones 🎁
                </p>
              </div>}
          </div>
        </section>

        {/* Entrega */}
        {tableNumber ? <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-804">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-805">
              {t('deliveryType')}
            </h3>
            <div className="flex items-center gap-3 mt-3 bg-dorado/10 border border-dorado/25 rounded-xl p-4" data-id="element-806">
              <span className="text-2xl" data-id="element-807">🍽️</span>
              <div data-id="element-808">
                <p className="text-dorado font-bold text-lg" data-id="element-809">
                  Mesa {tableNumber}
                </p>
                <p className="text-gray-400 text-xs" data-id="element-810">
                  Tu pedido será servido en tu mesa
                </p>
              </div>
            </div>
          </section> : <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-811">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-812">
              {t('deliveryType')}
            </h3>
            <p className="text-xs text-gray-400 mb-4" data-id="element-813">{t('deliveryChoose')}</p>
            <div className="flex gap-3 mb-4" data-id="element-814">
              {['Retiro en local', 'Express'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            entrega: tipo as any
          })} className={`flex-1 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.entrega === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-815">
                  {tipo === 'Retiro en local' ? t('pickupLocal') : t('express')}
                </button>)}
            </div>

            {data.entrega === 'Retiro en local' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg flex items-start gap-2 border border-gray-800" data-id="element-816">
                <span className="text-base leading-none" data-id="element-817">📍</span>
                {t('pickupHint')}
              </p>}

            {data.entrega === 'Express' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="space-y-3" data-id="element-818">
                <p className="text-xs text-terracota bg-terracota/10 p-3 rounded-lg flex items-start gap-2 border border-terracota/20" data-id="element-819">
                  <span className="text-base leading-none" data-id="element-820">🛵</span>
                  {t('expressCharge')} {currencySymbol}
                  {deliveryFee.toLocaleString()}
                </p>

                <div data-id="element-821">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-822">
                    {t('exactAddress')}{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-823">
                      {t('addressOptional')}
                    </span>
                  </label>
                  <textarea value={data.direccion} onChange={e => setData({
              ...data,
              direccion: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder={t('addressPlaceholder')} data-id="element-824" />
                </div>
                <div className="pt-2" data-id="element-825">
                  <label className="block text-xs font-medium text-gray-400 mb-2" data-id="element-826">
                    {t('mapLocation')}{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-827">
                      {t('mapRecommended')}
                    </span>
                  </label>
                  <LocationPicker onConfirm={url => setData({
              ...data,
              ubicacion: url
            })} initialLocation={data.ubicacion} data-id="element-828" />
                </div>
              </motion.div>}
          </section>}

        {/* Pago */}
        {tableNumber ? <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-829">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-830">
              {t('paymentMethod')}
            </h3>
            <div className="flex items-center gap-3 mt-3 bg-verde/10 border border-verde/25 rounded-xl p-4" data-id="element-831">
              <span className="text-2xl" data-id="element-832">💳</span>
              <div data-id="element-833">
                <p className="text-verde font-bold" data-id="element-834">Pago en mesa</p>
                <p className="text-gray-400 text-xs" data-id="element-835">
                  Coordiná el pago directamente con tu mesero
                </p>
              </div>
            </div>
          </section> : <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-836">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-837">
              {t('paymentMethod')}
            </h3>
            <p className="text-xs text-gray-400 mb-4" data-id="element-838">{t('paymentChoose')}</p>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-839">
              {['SINPE', 'Tarjeta', 'Efectivo'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            metodoPago: tipo as any
          })} className={`py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.metodoPago === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-840">
                  {tipo === 'Tarjeta' ? t('card') : tipo === 'Efectivo' ? t('cash') : tipo}
                </button>)}
            </div>

            {data.metodoPago === 'SINPE' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-841">
                {t('sinpeHint')}
              </p>}
            {data.metodoPago === 'Tarjeta' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-842">
                {t('cardHint')}
              </p>}

            {data.metodoPago === 'Efectivo' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} data-id="element-843">
                <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-844">
                  {t('payWith')}{' '}
                  <span className="text-xs text-gray-500 font-normal" data-id="element-845">
                    {t('payWithOptional')}
                  </span>
                </label>
                <input type="number" value={data.montoEfectivo} onChange={e => setData({
            ...data,
            montoEfectivo: e.target.value
          })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('payWithPlaceholder')} data-id="element-846" />
                {data.montoEfectivo && parseInt(data.montoEfectivo) > totalFinal && <p className="text-sm text-gray-400 mt-2" data-id="element-847">
                      {t('estimatedChange')}{' '}
                      <span className="font-semibold text-white" data-id="element-848">
                        {currencySymbol}
                        {parseInt(data.montoEfectivo) - totalFinal}
                      </span>
                    </p>}
                {!data.montoEfectivo && <p className="text-xs text-gray-500 mt-1" data-id="element-849">
                    {t('changeHint')}
                  </p>}
              </motion.div>}
          </section>}

        {/* Extras */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-850">
          <div className="flex items-center gap-2 mb-1" data-id="element-851">
            <h3 className="font-heading font-semibold text-white tracking-wide" data-id="element-852">
              {t('additionalComments')}
            </h3>
            <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-853">
              {t('optional')}
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4" data-id="element-854">{t('noCommentsHint')}</p>
          <div className="space-y-3" data-id="element-855">
            <div data-id="element-856">
              <label className="block text-xs font-medium text-red-400 mb-1" data-id="element-857">
                {t('allergies')}
              </label>
              <input type="text" value={data.alergias} onChange={e => setData({
              ...data,
              alergias: e.target.value
            })} className="w-full p-3 bg-red-950/20 border border-red-900/50 rounded-xl focus:ring-2 focus:ring-red-500 outline-none placeholder-red-900/50 text-red-400" placeholder={t('allergiesPlaceholder')} data-id="element-858" />
              <p className="text-xs text-red-500/70 mt-1" data-id="element-859">
                {t('allergiesWarning')}
              </p>
            </div>
            <div data-id="element-860">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-861">
                {t('restaurantNotes')}
              </label>
              <textarea value={data.comentarios} onChange={e => setData({
              ...data,
              comentarios: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder={t('restaurantNotesPlaceholder')} data-id="element-862" />
            </div>
          </div>
        </section>
      </div>

      {/* Footer Sticky */}
      <div className="bg-surface border-t border-gray-800 p-4 pb-safe fixed bottom-0 left-0 right-0 max-w-md mx-auto z-20" data-id="element-863">
        <div className="flex justify-between items-center mb-3 px-2" data-id="element-864">
          <span className="text-gray-400 font-medium" data-id="element-865">{t('totalToPay')}</span>
          <div className="flex flex-col items-end" data-id="element-866">
            <span className="font-bold text-2xl text-white" data-id="element-867">
              {totalFormatted.primary}
            </span>
            <span className="text-sm text-gray-400" data-id="element-868">
              {totalFormatted.secondary}
            </span>
          </div>
        </div>
        <button onClick={handleSendOrder} disabled={!isValid || isGenerating} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all border border-white/10 ${isValid && !isGenerating ? tableNumber ? 'bg-dorado text-carbon hover:bg-dorado/90 shadow-lg shadow-dorado/20 active:scale-95' : 'bg-[#25D366] text-white hover:bg-[#1ebd5a] shadow-lg shadow-[#25D366]/20 active:scale-95' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-869">
          {isGenerating ? <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-870" />
              {t('generatingTicket')}
            </> : tableNumber ? <>🍽️ Enviar pedido a cocina</> : <>
              <SendIcon className="w-5 h-5" data-id="element-871" />
              Enviar mi pedido por WhatsApp
            </>}
        </button>
        {!isValid && <p className="text-center text-xs text-red-500 mt-2" data-id="element-872">
            {data.nombre.trim() === '' && t('completeName') + ' '}
            {data.telefono.trim() === '' && t('addPhone') + ' '}
            {items.length === 0 && t('addProduct') + ' '}
            {!tableNumber && data.metodoPago === '' && '• Seleccioná un método de pago '}
            {!tableNumber && data.entrega === 'Express' && data.ubicacion === '' && '• Marcá tu ubicación en el mapa '}
          </p>}
      </div>

      {/* Hidden ticket for rendering */}
      <div style={{
      position: 'absolute',
      left: '-9999px',
      top: 0
    }} data-id="element-873">
        <OrderTicket ref={ticketRef} orderNumber={orderNumber} data={data} items={items} cartTotal={cartTotal} totalFinal={totalFinal} data-id="element-874" />
      </div>

      {/* Demand Popup */}
      <AnimatePresence data-id="element-875">
        {showDemandPopup && !demandAcknowledged && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[60] bg-carbon/90 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-876">
            <motion.div initial={{
          scale: 0.9,
          y: 20,
          opacity: 0
        }} animate={{
          scale: 1,
          y: 0,
          opacity: 1
        }} exit={{
          scale: 0.9,
          y: 20,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25
        }} className={`bg-surface border rounded-2xl p-6 max-w-sm w-full shadow-2xl ${demandLevel === 'very-high' ? 'border-red-500/30' : 'border-amber-500/30'}`} data-id="element-877">
              <div className="flex flex-col items-center text-center" data-id="element-878">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 border-2 ${demandLevel === 'very-high' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-amber-500/10 border-amber-500/20 text-amber-500'}`} data-id="element-879">
                  {demandLevel === 'very-high' ? <AlertTriangleIcon className="w-8 h-8" data-id="element-880" /> : <ClockIcon className="w-8 h-8" data-id="element-881" />}
                </div>
                <h3 className="font-heading text-xl text-white mb-2" data-id="element-882">
                  {demandLevel === 'very-high' ? '¡Cocina al máximo! 🔥' : 'Alta demanda 🔥'}
                </h3>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed" data-id="element-883">
                  {demandLevel === 'very-high' ? 'Tenemos muchos pedidos en este momento. Tu pedido puede demorar hasta 45 minutos. ¿Deseas continuar?' : 'Tenemos muchos pedidos en este momento. Tu pedido puede demorar un poco más de lo habitual.'}
                </p>
                <div className="flex flex-col w-full gap-3" data-id="element-884">
                  <button onClick={() => {
                setDemandAcknowledged(true);
                setShowDemandPopup(false);
              }} className={`w-full py-3 rounded-xl font-semibold transition-colors ${demandLevel === 'very-high' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`} data-id="element-885">
                    {demandLevel === 'very-high' ? 'Sí, continuar' : 'Entendido, continuar'}
                  </button>
                  {demandLevel === 'very-high' && <button onClick={() => {
                clearCart();
                onBack();
              }} className="w-full py-3 rounded-xl font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-id="element-886">
                      Abandonar pedido
                    </button>}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Confirmation Screen */}
      <AnimatePresence data-id="element-887">
        {showConfirmation && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[60] bg-carbon flex flex-col items-center justify-center max-w-md mx-auto px-8" data-id="element-888">
            <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          damping: 15,
          delay: 0.2
        }} className="mb-6" data-id="element-889">
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center border-2 border-emerald-500/30" data-id="element-890">
                <CheckCircleIcon className="w-10 h-10 text-emerald-500" data-id="element-891" />
              </div>
            </motion.div>

            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-center space-y-2" data-id="element-892">
              <h2 className="font-heading text-2xl text-white tracking-wide" data-id="element-893">
                {tableNumber ? '¡Pedido enviado a cocina!' : '¡Pedido registrado!'}
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-894">Pedido #{orderNumber}</p>
              {tableNumber && <p className="text-dorado text-sm font-medium mt-1" data-id="element-895">
                  🍽️ Mesa {tableNumber} · ~{estimatedMinutes} min
                </p>}
            </motion.div>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="mt-8 w-full max-w-xs flex flex-col items-center gap-3" data-id="element-896">
              {tableNumber ? <div className="w-full bg-dorado/10 border border-dorado/25 rounded-xl p-5" data-id="element-897">
                  <p className="text-white font-bold text-lg mb-2" data-id="element-898">
                    ¡Tu pedido está en camino! 🔥
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-899">
                    La cocina ya recibió tu pedido. Te lo llevaremos a tu mesa
                    en aproximadamente {estimatedMinutes} minutos.
                  </p>
                </div> : !whatsappSent ? <div className="w-full bg-surface-light border border-gray-700 rounded-xl p-5" data-id="element-900">
                  <p className="text-white font-bold text-lg mb-4" data-id="element-901">
                    Para completar tu pedido:
                  </p>
                  <div className="space-y-3" data-id="element-902">
                    <div className="flex items-start gap-3" data-id="element-903">
                      <span className="w-6 h-6 bg-[#25D366]/20 text-[#25D366] rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" data-id="element-904">
                        1
                      </span>
                      <p className="text-gray-300 text-sm" data-id="element-905">
                        Tocá el botón{' '}
                        <span className="text-[#25D366] font-semibold" data-id="element-906">
                          Enviar por WhatsApp
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-907">
                      <span className="w-6 h-6 bg-gray-600/30 text-gray-500 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" data-id="element-908">
                        2
                      </span>
                      <p className="text-gray-500 text-sm" data-id="element-909">
                        Volvé aquí y aparecerá el link para rastrear tu pedido
                      </p>
                    </div>
                  </div>
                </div> : <div className="w-full bg-surface-light border border-dorado/30 rounded-xl p-5" data-id="element-910">
                  <p className="text-white font-bold text-lg mb-2" data-id="element-911">
                    ¡Pedido enviado! 🎉
                  </p>
                  <p className="text-gray-400 text-sm" data-id="element-912">
                    Ahora podés rastrear el estado de tu pedido en tiempo real.
                  </p>
                </div>}

              {!tableNumber && whatsappUrl && !whatsappSent && <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => setWhatsappSent(true)} className="w-full px-6 py-4 bg-[#25D366] text-white font-bold rounded-xl flex items-center justify-center gap-3 active:scale-95 transition-transform shadow-lg shadow-[#25D366]/20" data-id="element-913">
                  <SendIcon className="w-5 h-5" data-id="element-914" />
                  Enviar por WhatsApp
                </a>}

              {tableNumber && savedTrackingToken && <>
                  <button onClick={() => {
              clearCart();
              window.location.href = `/track/${savedTrackingToken}`;
            }} className="w-full px-6 py-4 bg-dorado text-carbon font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-dorado/20" data-id="element-915">
                    <PackageIcon className="w-5 h-5" data-id="element-916" />
                    Ver estado de mi pedido
                  </button>
                  <button onClick={() => {
              clearCart();
              onBack();
            }} className="w-full px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-917">
                    Pedir algo más para la mesa
                  </button>
                </>}

              {!tableNumber && whatsappSent && savedTrackingToken && <>
                  <div className="w-full bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-3 text-center" data-id="element-918">
                    <p className="text-[#25D366] font-semibold text-sm" data-id="element-919">
                      ✅ ¡Listo! Ahora podés rastrear tu pedido
                    </p>
                  </div>
                  <button onClick={() => {
              clearCart();
              window.location.href = `/track/${savedTrackingToken}`;
            }} className="w-full px-6 py-4 bg-dorado text-carbon font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-dorado/20" data-id="element-920">
                    <PackageIcon className="w-5 h-5" data-id="element-921" />
                    Rastrear mi pedido
                  </button>
                </>}

              {!tableNumber && <div className="w-full flex gap-3 mt-4" data-id="element-922">
                  <button onClick={() => window.close()} className="flex-1 px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-923">
                    Cerrar navegador
                  </button>
                  <button onClick={() => {
              clearCart();
              onBack();
            }} className="flex-1 px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-924">
                    Pedir algo más
                  </button>
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}